import { Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import Form from 'dashboard/layouts/FormLayout/Form';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { WorkflowRunManualResumeOption } from 'dashboard/models/workflow-run';
import Button from 'design-system/components/Button';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import { HStack } from 'design-system/components/Stack';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';

const StyledModalContainer = styled(ModalContainer)`
  padding-top: ${Spacing[0]};
`;

const StyledModalHeader = styled(ModalHeader)`
  padding: ${Spacing[3]} ${Spacing[4]};
`;

const StyledModalBody = styled(ModalBody)`
  padding: ${Spacing[5]};
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacing[0]};
`;

const FooterDivider = styled(Divider)`
  margin: ${Spacing[4]} -${Spacing[5]};
`;

interface FormData {
  runType: WorkflowRunManualResumeOption;
}

interface WorkflowRunsRerunModalProps {
  onClose: () => void;
  onSubmit: (runType: string) => void;
  isLoading: boolean;
}

const WorkflowRunsRerunModal: VFC<WorkflowRunsRerunModalProps> = ({
  onClose,
  onSubmit,
  isLoading,
}) => {
  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>();

  const handleFormSubmit = (formData: FormData) => {
    onSubmit(formData.runType);
    onClose();
  };

  return (
    <StyledModalContainer>
      <StyledModalHeader onClose={onClose}>Rerun workflows</StyledModalHeader>
      <StyledDivider full />
      <StyledModalBody>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <RadioGroup>
            <Radio
              label='Resume from last failed step. This actions resumes errored Workflows from their last errored step.'
              value={WorkflowRunManualResumeOption.Resume}
              {...register('runType', { required: true })}
            />
            <Radio
              label='Rerun using latest workflow version. This action generates new runs.'
              value={WorkflowRunManualResumeOption.RerunWithLatestVersion}
              {...register('runType', { required: true })}
            />
          </RadioGroup>
          <FooterDivider full />
          <HStack spacing={Spacing[2]} justify='flex-end'>
            <Button
              width='auto'
              variant='basic'
              id='workflow-runs-rerun-modal__form-cancel-button'
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              width='auto'
              type='submit'
              variant='primary'
              isPending={isLoading}
              disabled={!isValid || isLoading}
              id='workflow-runs-rerun-modal__form-submit-button'
            >
              Rerun
            </Button>
          </HStack>
        </Form>
      </StyledModalBody>
    </StyledModalContainer>
  );
};

export default WorkflowRunsRerunModal;
